import React, { useCallback, useState, useRef } from 'react';
import { View, Text } from 'react-native';
import { useFocusEffect } from '@react-navigation/core';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from 'expo-linear-gradient';
import MaskedView from '@react-native-masked-view/masked-view';
import { useColorScheme } from 'nativewind';
import { AnimatedBirthdayLogo, Layout, Loader } from '../../components';
import client from '../../client/client';
import {
  StackNavigationScreenProps,
  NavigationParamList,
  ScreenName,
  StackName,
} from '../../navigation/types';
import { useMessage } from '../../contexts/Messages/MessageContext';
import { ErrorType } from '../../resources/interfaces';
import { isIos } from '../../utils/responsive';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { PersonaType } from '../../client/interfaces';

const BirthdayScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.BirthdayScreen>
> = ({ route, navigation }) => {
  const { notificationId } = route.params; // <<< PARAMÉTER
  const { t } = useTranslation();
  const [birthdayData, setBirthdayData] = useState(null);
  const { setMessage } = useMessage();
  const contentRef = useRef(null);
  const { colorScheme } = useColorScheme();
  const { i18n } = useTranslation();
  const { me } = useAuth();

  const fetchData = async () => {
    try {
      if (me?.personas?.[0]?.personaType !== PersonaType.employee) return;

      const res = await client.getBirthdayDetail(notificationId); // <<< BACKEND
      setBirthdayData(res);

      navigation.setOptions({
        title: t('birthday:title'),
      });
    } catch (error) {
      const notFoundErrors = [404, 422];
      setMessage({ message: error.message, type: 'error' });

      navigation.navigate(StackName.ErrorStack, {
        type: notFoundErrors.includes(error.response?.status)
          ? ErrorType.not_found
          : ErrorType.internal_server_error,
      });

      navigation.goBack();
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [notificationId]),
  );

  if (!birthdayData) {
    return (
      <View className="h-full w-full bg-neutral-50 dark:bg-neutral-950 items-center justify-center">
        <Loader />
      </View>
    );
  }

  return (
    <Layout isScrollView={false} refProp={contentRef} titleMarginBottom={false}>
      <View className="flex-1 items-center justify-center text-neutral-950 dark:text-neutral-50 pt-24">
        <View className="flex-row items-center justify-center overflow-hidden pt-4 pb-[14px] absolute top-[55%] translate-y-[-45%]">
          <AnimatedBirthdayLogo isScreen={true} />
          <View>
            <Text className="text-neutral-950 dark:text-neutral-50 text-birthdayTitle font-[sans-600] tracking-widest pt-[4px] pb-[2px]">
              {' '}
              {t('birthday:title')}
            </Text>
          </View>
        </View>

        <View className="flex-row flex-wrap items-end relative mt-auto pb-10 px-8">
          <Text className="flex text-neutral-950 dark:text-neutral-50 font-[sans-400] text-body relative text-center pb-[2px]">
            {t('birthday:joined')} {/* Gradient text is its own inline box */}
            <View
              style={{
                transform:
                  i18n.language === 'hu'
                    ? `translate(-6px ${isIos ? 16 : 7.5}px)`
                    : `translate(0px ${isIos ? 16 : 7.5}px)`,
                paddingRight: i18n.language === 'hu' ? 0 : 4,
              }}
            >
              <MaskedView
                maskElement={
                  <Text style={{ fontSize: 16, lineHeight: 24, fontFamily: 'sans-600' }}>
                    {birthdayData.anniversary} {t('birthday:years')}
                  </Text>
                }
              >
                <LinearGradient
                  colors={colorScheme === 'dark' ? ['#edf115', '#1fffd7'] : ['#623BFF', '#1FFFD7']}
                  start={{ x: -1, y: 0 }}
                  end={{ x: 1, y: 0 }}
                >
                  <Text
                    style={{
                      fontSize: 16,
                      lineHeight: 24,
                      fontFamily: 'sans-700',
                      color: 'transparent',
                    }}
                  >
                    {birthdayData.anniversary} {t('birthday:years')}
                  </Text>
                </LinearGradient>
              </MaskedView>
            </View>
            {t('birthday:thanks')} {t('birthday:being_with_us')}
          </Text>
        </View>
      </View>
    </Layout>
  );
};

export default BirthdayScreen;
